import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { BUILDING } from "@/data/content";

const CATEGORIES = ["All", "Ventures", "Professional Work", "Creative Work", "Collaborations", "Strategic Interests"];

export default function Building() {
  const [cat, setCat] = useState("All");
  const filtered = useMemo(() => (cat === "All" ? BUILDING : BUILDING.filter((b) => b.category === cat)), [cat]);

  return (
    <PageShell
      testId="page-building"
      title="Building | Quentin Young"
      description="Ventures, collaborations, professional work, creative projects, and strategic interests shaped by Quentin Young's approach to systems, brand, culture, and execution."
      ogImage="/og-building.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Building", url: "/building" }]}
    >
      <section className="container-edge pt-16 md:pt-24 pb-12">
        <p className="eyebrow-gold mb-5">Building</p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance">
          Things worth building rarely arrive complete.
        </h1>
        <p className="mt-8 max-w-2xl font-serif text-lg md:text-xl text-paper-secondary leading-relaxed text-pretty">
          A slate of ventures, professional work, client work, collaborations, and long-horizon interests.
          Entries grow as the work earns them.
        </p>
      </section>

      <section className="border-y border-ink-line bg-ink-surface/40 sticky top-16 md:top-20 z-30 backdrop-blur-sm">
        <div className="container-edge py-4 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            <p className="eyebrow mr-2">Category</p>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                data-testid={`building-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border ${
                  cat === c ? "border-gold text-gold-warm" : "border-ink-line text-paper-secondary hover:text-paper-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink-base py-16 md:py-24">
        <div className="container-edge">
          <div className="flex items-baseline justify-between mb-10">
            <p className="eyebrow">Entries</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
              {filtered.length} of {BUILDING.length}
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-line">
            {filtered.map((b, i) => (
              <li key={b.slug} className="bg-ink-base relative group">
                <Link
                  to={`/building/${b.slug}`}
                  data-testid={`building-entry-${b.slug}`}
                  className="block p-8 md:p-10 min-h-[240px] flex flex-col justify-between hover:bg-ink-surface/40 transition-colors duration-500"
                >
                  <div>
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                        {String(i + 1).padStart(2, "0")} · {b.category}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold/70">{b.status}</p>
                    </div>
                    <h3 className="mt-5 font-serif text-2xl md:text-3xl text-paper-primary group-hover:text-gold-warm transition-colors duration-300">
                      {b.name}
                    </h3>
                    <p className="mt-3 text-paper-secondary text-sm md:text-base leading-relaxed">{b.lead}</p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-tertiary group-hover:text-paper-primary">
                    Open entry <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </span>
                </Link>
                {b.url && (
                  <a
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`building-extlink-${b.slug}`}
                    className="absolute bottom-8 right-8 md:bottom-10 md:right-10 z-10 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/70 hover:text-gold-warm transition-colors duration-300"
                    aria-label={`Visit ${b.name} website (opens in new tab)`}
                  >
                    Visit ↗
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Every venture points somewhere else."
        rooms={[
          { label: "Read the thinking behind the work", to: "/thinking", hint: "thinking" },
          { label: "Trace the evolution in artifacts", to: "/artifacts", hint: "long view" },
          { label: "Start a conversation", to: "/contact", hint: "reach out" },
        ]}
        testId="building-next"
      />
    </PageShell>
  );
}
